// A cost cell: the amount, the sign it prints with, and the caption line under
// it. Every value is edited in the cell that prints it, so the control set is
// the printed cell — an amount field, a three-sign toggle, and the cell's own
// second line.
import { useCallback, useRef, type RefObject } from 'react';
import { useTranslation } from 'react-i18next';
import { useFormContext, type RefCallBack } from 'react-hook-form';
import {
  formatSheetAmount,
  type LogisticsCalculationCurrency,
  type LogisticsCalculationEditableContent,
} from '@magimp/shared';
import {
  decimalPoint,
  useCell,
  useCellInvalid,
  useDocumentEditing,
  type DocumentPath,
} from './DocumentCell.js';
import type { PrintedAmount } from './document-format.js';
import type { Cost } from './sheet-amounts.js';

const SIGNS: Record<LogisticsCalculationCurrency, string> = { USD: '$', CNY: '¥', RUB: '₽' };
const CURRENCIES = ['USD', 'CNY', 'RUB'] as const;
const DASH = '—';

export interface CostCellProps {
  /** The cost object's own form path; the whole `{amount, currency}` is one cell. */
  name: DocumentPath;
  /** The printed column heading, which names every control inside the cell. */
  column: string;
  defaultCurrency: LogisticsCalculationCurrency;
  cost: Cost | null;
  captioned?: boolean;
  /** The example the empty caption line shows until the manager writes his own. */
  hint?: string | undefined;
}

/** One printed money value, with the sign where that currency puts it. */
export function Money({
  amount,
  currency,
  bold,
}: {
  amount: string;
  currency: LogisticsCalculationCurrency;
  bold?: boolean | undefined;
}) {
  return <MoneyText text={formatSheetAmount(amount, currency)} bold={bold} />;
}

export function MoneyText({ text: value, bold }: { text: string; bold?: boolean | undefined }) {
  return <span className={`doc-money doc-n${bold ? ' doc-b' : ''}`}>{value}</span>;
}

/** A frozen totals triple, one printed line per currency the document charges. */
export function Amounts({
  amounts,
  bold,
}: {
  amounts: readonly PrintedAmount[];
  bold?: boolean | undefined;
}) {
  if (amounts.length === 0) return <span className="doc-dash">{DASH}</span>;
  return (
    <>
      {amounts.map((amount) => (
        <MoneyText key={amount.currency} text={amount.text} bold={bold} />
      ))}
    </>
  );
}

export function CostCell(props: CostCellProps) {
  return useDocumentEditing() ? <EditableCost {...props} /> : <PrintedCost cost={props.cost} />;
}

function PrintedCost({ cost }: { cost: Cost | null }) {
  if (!cost || cost.amount === '') return <span className="doc-dash">{DASH}</span>;
  return (
    <>
      <Money amount={cost.amount} currency={cost.currency} />
      {cost.caption ? <span className="doc-sm">{cost.caption}</span> : null}
    </>
  );
}

/**
 * INVARIANT: an untouched cost stays absent from the stored cargo. Nothing here
 * runs until the manager types in the cell, so an ordinary save of a legacy
 * document does not materialise the new shape on every cargo it carries.
 *
 * WHY an emptied cell keeps its object: react-hook-form reads a controller's
 * value through `useWatch`, which answers `undefined` with the field's default —
 * so writing `undefined` would make a cleared cell snap back to the amount it
 * was loaded with.
 */
function EditableCost({ name, column, defaultCurrency, captioned, hint }: CostCellProps) {
  const { t } = useTranslation('logisticsCalculations');
  const field = useCell(name);
  const captionInvalid = useCellInvalid(`${name}.caption` as DocumentPath);
  const caption = useRef<HTMLInputElement>(null);
  const amountRef = useLeafFocus(name, field.ref, caption);
  const value = (field.value ?? undefined) as Cost | undefined;
  const currency = value?.currency ?? defaultCurrency;
  const write = (next: Partial<Cost>) => {
    field.onChange({ ...value, amount: value?.amount ?? '', currency, ...next } satisfies Cost);
  };
  return (
    <>
      <span className="doc-cost-line">
        <AmountField
          name={name}
          amount={value?.amount ?? ''}
          currency={currency}
          label={t('editor.cost.amount', { column })}
          inputRef={amountRef}
          onAmountChange={(amount) => write({ amount })}
        />
        <CurrencyToggle
          column={column}
          current={currency}
          onSelect={(next) => write({ currency: next })}
        />
      </span>
      {/* The caption is the cell's second line, as it prints: an aside under the
          figure, set apart from it so it never reads as a second amount. */}
      {captioned ? (
        <input
          className="doc-input doc-caption-input"
          ref={caption}
          aria-label={t('editor.cost.caption', { column })}
          aria-invalid={captionInvalid || undefined}
          {...(hint === undefined ? {} : { placeholder: hint })}
          value={value?.caption ?? ''}
          onChange={(event) => write({ caption: event.target.value })}
          onBlur={field.onBlur}
        />
      ) : null}
    </>
  );
}

/**
 * The amount input's ref, and with it the cell's answer to which of its two
 * controls a refused save was about.
 *
 * WHY not a controller per leaf: registering `<cost>.amount` or
 * `<cost>.caption` writes that path into the form values and would materialise
 * a cost object on every cell the manager never touched — the invariant above.
 * The cell keeps its single registration and hands react-hook-form a target
 * that reads the refused leaf at the moment it is asked to focus, which is
 * after the errors are set and before the cells re-render carrying them.
 */
function useLeafFocus(
  name: DocumentPath,
  register: RefCallBack,
  caption: RefObject<HTMLInputElement>,
): RefCallBack {
  const { getFieldState } = useFormContext<LogisticsCalculationEditableContent>();
  const amount = useRef<HTMLInputElement | null>(null);
  return useCallback(
    (element: HTMLInputElement | null) => {
      amount.current = element;
      register({
        focus: () => {
          const refused = getFieldState(`${name}.caption` as DocumentPath).invalid
            ? caption.current
            : null;
          (refused ?? amount.current)?.focus();
        },
      });
    },
    [caption, getFieldState, name, register],
  );
}

/** The sign sits where it prints, so the manager sees the cell he is making. */
function AmountField({
  name,
  amount,
  currency,
  label,
  inputRef,
  onAmountChange,
}: {
  name: DocumentPath;
  amount: string;
  currency: LogisticsCalculationCurrency;
  label: string;
  inputRef: RefCallBack;
  onAmountChange: (amount: string) => void;
}) {
  const invalid = useCellInvalid(`${name}.amount` as DocumentPath);
  const sign = <span className="doc-sign">{SIGNS[currency]}</span>;
  const input = (
    <input
      className="doc-input doc-amount-input"
      ref={inputRef}
      aria-label={label}
      aria-invalid={invalid || undefined}
      inputMode="decimal"
      placeholder="0"
      value={amount}
      onChange={(event) => onAmountChange(decimalPoint(event.target.value))}
    />
  );
  return (
    <span className="doc-cost-amount doc-n">
      {currency === 'RUB' ? (
        <>
          {input}
          {sign}
        </>
      ) : (
        <>
          {sign}
          {input}
        </>
      )}
    </span>
  );
}

function CurrencyToggle({
  column,
  current,
  onSelect,
}: {
  column: string;
  current: LogisticsCalculationCurrency;
  onSelect: (currency: LogisticsCalculationCurrency) => void;
}) {
  const { t } = useTranslation('logisticsCalculations');
  return (
    <span
      className="doc-cur"
      role="group"
      aria-label={t('editor.cost.currency', { column })}
      data-editing=""
    >
      {CURRENCIES.map((currency) => (
        <button
          key={currency}
          type="button"
          className="doc-cur-sign"
          // WHY: three buttons per money cell would make Tab stop eighteen extra
          // times a row. The mouse and the accessibility tree still reach them.
          tabIndex={-1}
          aria-pressed={currency === current}
          onClick={() => onSelect(currency)}
        >
          {SIGNS[currency]}
        </button>
      ))}
    </span>
  );
}

/** The em dash a cell prints when the document charges nothing for it. */
export function CostDash() {
  return <span className="doc-dash">{DASH}</span>;
}
